import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { caseApplicationsService, GET_PINNED_CASES } from '@/api';
import { Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import AppRoutes from '@/const/routes';
import { RoleType } from '@/interfaces';
import { casesLawyerService } from '@/api/casesLawyer';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { useState } from 'react';
import clsx from 'clsx';
interface PinnedCasesI {
	role: RoleType;
}

export const PinnedCases: React.FC<PinnedCasesI> = ({ role }) => {
	const [open, setOpen] = useState<boolean>(false);

	const { data: pinnedCases } = useQuery({
		queryKey: [GET_PINNED_CASES, role],
		queryFn: () =>
			role === 'client'
				? caseApplicationsService.getPinnedCases()
				: casesLawyerService.getPinnedLawyerCases(),
	});

	const listCases = pinnedCases?.data || [];

	const handleOpen = (): void => {
		setOpen(true);
	};

	const handleClose = (): void => {
		setOpen(false);
	};

	const content = (
		<ul className="space-y-4">
			{listCases.map((item) => (
				<Link
					className="flex items-center mx-auto gap-2 cursor-pointer"
					href={
						role === 'client'
							? `${AppRoutes.CLIENTAPP_MY_CASES}/${item.id}`
							: `${AppRoutes.LAWYERAPP_CASES}/${item.id}/clients`
					}
					key={item.id}
				>
					<div>
						<Icon src={Icons.pinFulled} className="!size-4 text-green-base" />
					</div>
					<Typography type="regular-14" className="truncate">
						{item.title}
					</Typography>
				</Link>
			))}
		</ul>
	);

	return (
		<>
			{/* // ------------ Mobile --------------// */}
			<div className="block lg:hidden px-6">
				<div className="flex justify-start items-center gap-x-1 mb-4">
					<Icon src={Icons.pinFulled} className="!size-5 text-green-base" />
					<Typography type="semibold-12" className="text-primary">
						Pinned Cases
					</Typography>
				</div>
				<div className="max-h-[132px] scroll-custom overflow-y-auto">
					{content}
				</div>
			</div>
			{/* // ----------------- Desktop ----------------// */}
			<div className="hidden lg:block">
				<TooltipProvider delayDuration={0}>
					<Tooltip open={open}>
						{listCases.length > 0 && (
							<div className="px-2 lg:mt-10 mb-2" onMouseLeave={handleClose}>
								<TooltipTrigger
									asChild
									onMouseEnter={handleOpen}
									className={clsx('z-10')}
								>
									<div className="flex flex-col justify-center items-center">
										<Icon
											src={Icons.pinFulled}
											className="!size-5 text-green-base"
										/>
										<Typography
											type="semibold-12"
											className="text-primary mt-1 text-center"
										>
											Pinned Cases
										</Typography>
									</div>
								</TooltipTrigger>
								<RadixTooltip.Portal>
									<TooltipContent
										align="end"
										side="right"
										className={clsx(
											'flex items-center p-4 gap-1 bg-white w-[232px] rounded-3xl'
										)}
									>
										{content}
									</TooltipContent>
								</RadixTooltip.Portal>
							</div>
						)}
					</Tooltip>
				</TooltipProvider>
			</div>
		</>
	);
};
